// ═══════════════════════════════════════════════════════════════
// Chat Context — parse URL location + contextual suggestions
// ═══════════════════════════════════════════════════════════════

export interface LocationContext {
  modulo: string;
  vista: string;
  entidadId?: string;
  extra?: string;
}

export interface ChatSuggestion {
  icon: string;
  text: string;
}

/**
 * Parses the current pathname into a structured context object.
 * Examples:
 *   /process-map/process/abc-123  → { modulo: 'process-map', vista: 'nivel3', entidadId: 'abc-123' }
 *   /documento/PE.21.NC.01        → { modulo: 'document', vista: 'viewer', entidadId: 'PE.21.NC.01' }
 *   /proceso/PE.21/ficha          → { modulo: 'process', vista: 'ficha', entidadId: 'PE.21' }
 *   /procesos                     → { modulo: 'process', vista: 'dashboard' }
 *   /admin/ia                     → { modulo: 'admin', vista: 'ia' }
 */
export function parseLocationContext(pathname: string, search?: string): LocationContext {
  const parts = pathname.split('/').filter(Boolean);
  const params = new URLSearchParams(search || '');

  // /process-map/process/:id
  if (parts[0] === 'process-map' && parts[1] === 'process' && parts[2]) {
    return { modulo: 'process-map', vista: 'nivel3', entidadId: parts[2] };
  }
  // /process-map/group/:grupoNumero?tip=PE|PO|PS
  if (parts[0] === 'process-map' && parts[1] === 'group' && parts[2]) {
    return {
      modulo: 'process-map',
      vista: 'nivel2',
      entidadId: parts[2],
      extra: params.get('tip') || undefined,
    };
  }
  // /process-map (level 1)
  if (parts[0] === 'process-map') {
    return { modulo: 'process-map', vista: parts[1] ?? 'mapa' };
  }

  // /documento/:codigo
  if (parts[0] === 'documento' && parts[1]) {
    return { modulo: 'document', vista: 'viewer', entidadId: parts[1] };
  }

  // /proceso/:codigo/ficha
  if (parts[0] === 'proceso' && parts[1]) {
    return { modulo: 'process', vista: parts[2] ?? 'ficha', entidadId: parts[1] };
  }

  // /procesos
  if (parts[0] === 'procesos') {
    return { modulo: 'process', vista: 'dashboard' };
  }

  // /structure/personas/:id or /structure/puestos/:id
  if (parts[0] === 'structure') {
    const tab = params.get('tab') || 'analytics';
    if (parts[1] === 'personas' && parts[2]) {
      return { modulo: 'structure', vista: 'persona-detalle', entidadId: parts[2] };
    }
    if (parts[1] === 'puestos' && parts[2]) {
      return { modulo: 'structure', vista: 'puesto-detalle', entidadId: parts[2] };
    }
    return { modulo: 'structure', vista: tab };
  }

  // Legacy: /personas/:id
  if (parts[0] === 'personas') {
    return { modulo: 'structure', vista: 'personas', entidadId: parts[1] };
  }

  // Legacy: /estructura, /organigrama, /sedes
  if (['estructura', 'organigrama', 'sedes', 'entidades'].includes(parts[0])) {
    return { modulo: 'structure', vista: parts[0] };
  }

  // /admin — read tab from URL or path segment
  if (parts[0] === 'admin') {
    const tab = params.get('tab') || parts[1] || 'dashboard';
    return { modulo: 'admin', vista: tab };
  }

  // /process — read tab from URL
  if (parts[0] === 'process') {
    const tab = params.get('tab') || 'analytics';
    return { modulo: 'process', vista: tab };
  }

  // /compliance
  if (parts[0] === 'compliance') {
    const tab = params.get('tab') || 'general';
    return { modulo: 'compliance', vista: tab };
  }

  // /information
  if (parts[0] === 'information') {
    const tab = params.get('tab') || 'general';
    if (parts[1] === 'auditoria') return { modulo: 'information', vista: 'auditoria' };
    return { modulo: 'information', vista: tab };
  }

  // /reputation
  if (parts[0] === 'reputation') {
    const tab = params.get('tab') || 'general';
    return { modulo: 'reputation', vista: tab };
  }

  // /tech
  if (parts[0] === 'tech') {
    const tab = params.get('tab') || 'general';
    return { modulo: 'tech', vista: tab };
  }

  return { modulo: 'general', vista: 'home' };
}

// ─── Suggestion pools by module ──────────────────────────────────────────────

const SUGGESTIONS: Record<string, ChatSuggestion[]> = {
  'process-map': [
    { icon: '🗺️', text: '¿Cuántos procesos tiene el mapa y cómo se distribuyen por tipología?' },
    { icon: '📊', text: '¿Cuáles son los procesos estratégicos y quién los dirige?' },
    { icon: '🔄', text: '¿Qué procesos operativos tienen flujograma publicado?' },
    { icon: '📋', text: '¿Cuántos documentos normativos están vinculados a cada proceso?' },
    { icon: '👤', text: '¿Qué procesos no tienen responsable asignado?' },
    { icon: '⚙️', text: '¿Cuáles son los procesos de soporte y qué herramientas usan?' },
  ],
  document: [
    { icon: '📄', text: '¿Qué dice este documento y cuál es su alcance?' },
    { icon: '📋', text: '¿Cuáles son los requisitos clave de este documento?' },
    { icon: '🔍', text: '¿Qué normas aplican a este documento?' },
    { icon: '👥', text: '¿Quién es el responsable de revisar este documento?' },
    { icon: '📊', text: '¿Qué otros documentos están relacionados con este?' },
    { icon: '✅', text: '¿Este documento cumple con el formato normativo requerido?' },
  ],
  process: [
    { icon: '📊', text: '¿Cuántos procesos activos hay en el sistema?' },
    { icon: '🔄', text: '¿Qué procesos están en estado de revisión?' },
    { icon: '📈', text: '¿Cuál es el estado general de los flujogramas?' },
    { icon: '👤', text: '¿Qué responsables tienen más procesos a cargo?' },
    { icon: '📋', text: 'Dame un resumen de los procesos por tipología' },
    { icon: '⚠️', text: '¿Hay procesos sin documentos normativos vinculados?' },
  ],
  structure: [
    { icon: '🏗️', text: '¿Cómo está organizada la estructura jerárquica?' },
    { icon: '👥', text: '¿Cuántas personas hay por departamento?' },
    { icon: '📊', text: 'Dame un resumen del organigrama actual' },
    { icon: '🏢', text: '¿Cuántas sedes tiene la organización?' },
    { icon: '👤', text: '¿Quién es el CEO y cuál es su equipo directo?' },
    { icon: '📋', text: '¿Qué entidades conforman el grupo empresarial?' },
  ],
  'structure:analytics': [
    { icon: '📊', text: '¿Cuántos puestos hay sin titular asignado?' },
    { icon: '🏢', text: '¿Cómo se distribuyen las personas por departamento?' },
    { icon: '👤', text: '¿Qué dirección tiene más procesos asignados?' },
    { icon: '📈', text: '¿Cuál es el nivel de cobertura de gobernanza?' },
    { icon: '⚡', text: '¿Hay vacantes críticas en la organización?' },
    { icon: '🔍', text: '¿Cuál es el bus factor de cada área?' },
  ],
  'structure:organigrama': [
    { icon: '👥', text: '¿Quién reporta al Director General?' },
    { icon: '📊', text: '¿Cuántos niveles tiene el organigrama?' },
    { icon: '🏢', text: '¿Qué departamentos tiene la Dirección Tecnológica?' },
    { icon: '👤', text: '¿Cómo se distribuyen los puestos por dirección?' },
    { icon: '🔗', text: '¿Qué puestos tienen dependencia funcional cruzada?' },
    { icon: '⚡', text: '¿Qué nodos del organigrama no tienen titular?' },
  ],
  'structure:puestos': [
    { icon: '📋', text: '¿Cuántos puestos hay definidos en la organización?' },
    { icon: '👤', text: '¿Qué puestos tienen competencias de gobernanza?' },
    { icon: '🏢', text: '¿Cuáles son los puestos de dirección?' },
    { icon: '⚡', text: '¿Qué puestos no tienen titular asignado?' },
    { icon: '📊', text: '¿Cómo se distribuyen los puestos por departamento?' },
    { icon: '🔗', text: '¿Qué puestos son responsables de proceso?' },
  ],
  'structure:organizaciones': [
    { icon: '🏢', text: '¿Cuántas organizaciones tiene el grupo empresarial?' },
    { icon: '📊', text: '¿Cuántas personas tiene cada organización?' },
    { icon: '👥', text: '¿Qué empresas componen el grupo?' },
    { icon: '🔗', text: '¿Cómo se relacionan las organizaciones entre sí?' },
    { icon: '📋', text: '¿Qué centros de trabajo tiene cada organización?' },
    { icon: '⚡', text: '¿Qué organizaciones tienen procesos asignados?' },
  ],
  'structure:personas': [
    { icon: '👥', text: '¿Cuántas personas hay en la organización?' },
    { icon: '📊', text: '¿Cómo se distribuyen por departamento?' },
    { icon: '👤', text: '¿Quién es responsable de más procesos?' },
    { icon: '🏢', text: '¿Qué personas tienen rol de gobernanza?' },
    { icon: '📋', text: '¿Qué personas tienen cargos de dirección?' },
    { icon: '⚡', text: '¿Hay personas sin puesto asignado?' },
  ],
  'structure:gobernanza': [
    { icon: '🏛️', text: '¿Qué roles de gobernanza existen en la organización?' },
    { icon: '👤', text: '¿Qué puestos tienen rol de gobernanza asignado?' },
    { icon: '📊', text: '¿Cuántos roles de gobernanza hay cubiertos?' },
    { icon: '⚡', text: '¿Hay roles de gobernanza sin persona asignada?' },
    { icon: '🔗', text: '¿Cómo se vinculan los roles de gobernanza con los procesos?' },
    { icon: '📋', text: '¿Cuáles son las entidades de gobernanza del sistema?' },
  ],
  'structure:administracion': [
    { icon: '⚙️', text: '¿Cuántos departamentos hay configurados?' },
    { icon: '🏢', text: '¿Qué centros de trabajo existen?' },
    { icon: '👤', text: '¿Cómo se gestionan las asignaciones persona-puesto?' },
    { icon: '📊', text: '¿Qué tipos de centro de trabajo hay?' },
    { icon: '📋', text: '¿Cómo se configura la estructura organizativa?' },
    { icon: '🔗', text: '¿Qué relación hay entre departamentos y procesos?' },
  ],
  'structure:persona-detalle': [
    { icon: '👤', text: '¿Qué puesto ocupa esta persona?' },
    { icon: '📋', text: '¿De qué procesos es responsable?' },
    { icon: '🏢', text: '¿A qué departamento pertenece?' },
    { icon: '🔗', text: '¿Qué roles de gobernanza tiene asignados?' },
    { icon: '📄', text: '¿Qué documentos están vinculados a esta persona?' },
    { icon: '📊', text: '¿En qué grupo funcional participa?' },
  ],
  'structure:puesto-detalle': [
    { icon: '👤', text: '¿Quién ocupa este puesto actualmente?' },
    { icon: '📋', text: '¿Qué procesos tiene asignados este puesto?' },
    { icon: '🏢', text: '¿A qué departamento pertenece?' },
    { icon: '🔗', text: '¿Qué roles de gobernanza tiene este puesto?' },
    { icon: '📊', text: '¿De quién depende jerárquicamente?' },
    { icon: '⚡', text: '¿Hay personas suplentes para este puesto?' },
  ],
  admin: [
    { icon: '⚙️', text: '¿Qué proveedores de IA están configurados?' },
    { icon: '📊', text: '¿Cuántos tokens se han consumido este mes?' },
    { icon: '🔑', text: '¿Cuáles son los roles y permisos configurados?' },
    { icon: '🏢', text: '¿Qué configuración tiene el tenant actual?' },
  ],
  'admin:dashboard': [
    { icon: '📊', text: '¿Cuántos usuarios activos hay en la plataforma?' },
    { icon: '👥', text: '¿Cuántos tenants están configurados?' },
    { icon: '📈', text: '¿Cuál es la actividad reciente del sistema?' },
    { icon: '⚙️', text: '¿Hay algún problema de configuración pendiente?' },
  ],
  'admin:empresa': [
    { icon: '🏢', text: '¿Cuáles son los datos de la empresa configurados?' },
    { icon: '📋', text: '¿Qué configuración tiene el tenant actual?' },
    { icon: '🔗', text: '¿Qué organizaciones están vinculadas?' },
    { icon: '⚙️', text: '¿Cómo se configura la identidad corporativa?' },
  ],
  'admin:usuarios': [
    { icon: '👥', text: '¿Cuántos usuarios hay registrados?' },
    { icon: '🔐', text: '¿Qué roles de usuario existen en el sistema?' },
    { icon: '📊', text: '¿Cuántos usuarios tienen rol de administrador?' },
    { icon: '👤', text: '¿Qué permisos tiene cada rol?' },
  ],
  'admin:plataforma': [
    { icon: '⚙️', text: '¿Qué módulos están habilitados?' },
    { icon: '🏗️', text: '¿Cuál es la configuración de la plataforma?' },
    { icon: '📋', text: '¿Qué plan de licenciamiento tiene el tenant?' },
    { icon: '🔧', text: '¿Hay alguna configuración pendiente?' },
  ],
  'admin:ia': [
    { icon: '📊', text: '¿Cuántos tokens he consumido este mes?' },
    { icon: '💰', text: '¿Cuál es el coste estimado de la IA?' },
    { icon: '🔧', text: '¿Qué proveedores de IA están configurados?' },
    { icon: '📈', text: '¿Cuál es la tasa de éxito del RAG?' },
    { icon: '🤖', text: '¿Qué modelo se usa para el chat?' },
    { icon: '⚡', text: '¿Cuántos documentos están indexados en el RAG?' },
  ],
  'admin:notificaciones': [
    { icon: '🔔', text: '¿Qué tipos de notificación hay configurados?' },
    { icon: '📬', text: '¿Cuántas notificaciones se han enviado este mes?' },
    { icon: '⚙️', text: '¿Cómo se configuran las alertas del sistema?' },
    { icon: '📋', text: '¿Qué canales de notificación están activos?' },
  ],
  'process:analytics': [
    { icon: '📊', text: '¿Cuántos procesos hay en total y cuántos están documentados?' },
    { icon: '📈', text: '¿Cuál es el avance global de la documentación?' },
    { icon: '👤', text: '¿Qué dirección tiene más procesos asignados?' },
    { icon: '📋', text: '¿Qué procesos faltan por completar su ficha?' },
    { icon: '🔍', text: '¿Hay procesos sin responsable asignado?' },
    { icon: '⚡', text: '¿Cuáles son los próximos procesos a documentar?' },
  ],
  'process:explorador': [
    { icon: '📄', text: '¿Cuántos documentos hay en el sistema?' },
    { icon: '🔍', text: '¿Qué documentos están en borrador?' },
    { icon: '📊', text: '¿Cuántos documentos vigentes hay?' },
    { icon: '📝', text: '¿Qué documentos se actualizaron este mes?' },
    { icon: '🏗️', text: '¿Qué tipos documentales tiene el sistema?' },
    { icon: '⚡', text: 'Busca documentos que hablen de gobernanza' },
  ],
  'process:caracterizacion': [
    { icon: '📋', text: '¿Cuántos procesos tienen ficha de caracterización completa?' },
    { icon: '📊', text: '¿Qué campos faltan por completar en las fichas?' },
    { icon: '👤', text: '¿Qué procesos no tienen responsable definido?' },
    { icon: '🔄', text: '¿Qué procesos tienen entradas y salidas definidas?' },
    { icon: '📈', text: '¿Qué procesos tienen indicadores configurados?' },
    { icon: '⚡', text: '¿Cuáles son los procesos más documentados?' },
  ],
  'process:admin-procesos': [
    { icon: '⚙️', text: '¿Cómo se administran los procesos del mapa?' },
    { icon: '📋', text: '¿Cuántos procesos hay por tipología?' },
    { icon: '👥', text: '¿Qué grupos funcionales están definidos?' },
    { icon: '🔧', text: '¿Qué configuración tienen los procesos?' },
    { icon: '📊', text: '¿Cuántas fases del ciclo de vida están cubiertas?' },
    { icon: '⚡', text: '¿Qué procesos necesitan revisión?' },
  ],
  'compliance:general': [
    { icon: '✅', text: '¿Cuál es el estado de cumplimiento normativo?' },
    { icon: '📋', text: '¿Qué auditorías se han realizado?' },
    { icon: '🔍', text: '¿Qué workflows están en curso?' },
    { icon: '📊', text: '¿Cuál es el grado de cumplimiento documental?' },
  ],
  'information:general': [
    { icon: '📄', text: '¿Cuántos documentos hay en el sistema de gestión?' },
    { icon: '📊', text: '¿Cómo se distribuyen por tipo documental?' },
    { icon: '🔍', text: '¿Qué documentos necesitan actualización?' },
    { icon: '📋', text: '¿Cuál es el estado del cuadro de mando informacional?' },
  ],
  'reputation:general': [
    { icon: '📰', text: '¿Qué noticias corporativas hay publicadas?' },
    { icon: '📊', text: '¿Cuál es el alcance de las comunicaciones internas?' },
    { icon: '📋', text: '¿Qué eventos corporativos están planificados?' },
    { icon: '🔍', text: '¿Qué encuestas internas están activas?' },
  ],
  'tech:general': [
    { icon: '⚙️', text: '¿Qué configuración tecnológica tiene la plataforma?' },
    { icon: '🔧', text: '¿Qué módulos están habilitados?' },
    { icon: '📊', text: '¿Cuál es el estado de los servicios tecnológicos?' },
    { icon: '📋', text: '¿Qué proyectos tecnológicos están en curso?' },
  ],
  general: [
    { icon: '📊', text: '¿Cuántos procesos tiene el sistema y cómo se distribuyen?' },
    { icon: '👥', text: '¿Quién es el CTO y a qué departamento pertenece?' },
    { icon: '📄', text: '¿Qué documentos normativos tiene el proceso PE.21?' },
    { icon: '🏗️', text: '¿Cuáles son las seis ingenierías de gobernanza?' },
    { icon: '📋', text: 'Dame un resumen de la estructura organizativa' },
    { icon: '🔍', text: '¿Qué dice el documento PE.21.NC.01?' },
  ],
};

// ─── Shuffle helper ──────────────────────────────────────────────────────────

function shuffle<T>(arr: T[]): T[] {
  const s = [...arr];
  for (let i = s.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [s[i], s[j]] = [s[j], s[i]];
  }
  return s;
}

/**
 * Returns 4 randomized suggestions for the given module/vista context.
 * If `entidad` is provided, generates entity-specific questions.
 */
export function getSuggestions(modulo: string, vista: string, entidad?: string): ChatSuggestion[] {
  // Grupo funcional (nivel 2)
  if (modulo === 'process-map' && vista === 'nivel2' && entidad) {
    return shuffle([
      { icon: '\uD83D\uDCCB', text: `\u00bfQu\u00e9 procesos tiene el grupo ${entidad}?` },
      { icon: '\uD83D\uDC64', text: `\u00bfQui\u00e9n lidera el grupo ${entidad}?` },
      {
        icon: '\uD83D\uDCCA',
        text: `\u00bfCu\u00e1l es el avance de documentaci\u00f3n del grupo ${entidad}?`,
      },
      {
        icon: '\uD83D\uDCC4',
        text: `\u00bfQu\u00e9 documentos tienen los procesos de ${entidad}?`,
      },
      { icon: '\uD83D\uDD17', text: `\u00bfCon qu\u00e9 otros grupos se relaciona ${entidad}?` },
      { icon: '\u26A1', text: `\u00bfQu\u00e9 falta por documentar en ${entidad}?` },
      {
        icon: '\uD83D\uDC65',
        text: `\u00bfQui\u00e9nes son los responsables de los procesos de ${entidad}?`,
      },
      {
        icon: '\uD83C\uDFD7\uFE0F',
        text: `\u00bfQu\u00e9 tipolog\u00eda tiene ${entidad} y cu\u00e1ntos procesos incluye?`,
      },
    ]).slice(0, 4);
  }

  // Proceso específico
  if (modulo === 'process-map' && vista === 'nivel3' && entidad) {
    return shuffle([
      { icon: '\uD83D\uDCCB', text: `Describe el proceso ${entidad} y su objetivo` },
      { icon: '\uD83D\uDCC4', text: `\u00bfQu\u00e9 documentos tiene ${entidad}?` },
      { icon: '\uD83D\uDC64', text: `\u00bfQui\u00e9n es el responsable de ${entidad}?` },
      { icon: '\uD83D\uDD04', text: `\u00bfCu\u00e1les son las entradas y salidas de ${entidad}?` },
      { icon: '\uD83D\uDCCA', text: `\u00bfQu\u00e9 indicadores mide ${entidad}?` },
      { icon: '\u26A1', text: `\u00bfQu\u00e9 actividades tiene el flujograma de ${entidad}?` },
      {
        icon: '\uD83D\uDD17',
        text: `\u00bfQu\u00e9 procesos est\u00e1n relacionados con ${entidad}?`,
      },
      { icon: '\uD83D\uDCDD', text: `Resume la ficha de caracterizaci\u00f3n de ${entidad}` },
    ]).slice(0, 4);
  }

  // Documento específico
  if (modulo === 'document' && entidad) {
    return shuffle([
      { icon: '\uD83D\uDCD6', text: `Resume el contenido de ${entidad}` },
      {
        icon: '\uD83D\uDD0D',
        text: `\u00bfQu\u00e9 dice la secci\u00f3n principal de ${entidad}?`,
      },
      { icon: '\u2705', text: `Audita el documento ${entidad}` },
      { icon: '\uD83D\uDD17', text: `\u00bfQu\u00e9 referencias cruzadas tiene ${entidad}?` },
      { icon: '\uD83D\uDCCA', text: `\u00bfA qu\u00e9 proceso pertenece ${entidad}?` },
      { icon: '\uD83C\uDF10', text: `Traduce el resumen de ${entidad} al ingl\u00e9s` },
    ]).slice(0, 4);
  }

  // Try module:vista specific pool first, then module, then general
  const pool = SUGGESTIONS[`${modulo}:${vista}`] ?? SUGGESTIONS[modulo] ?? SUGGESTIONS.general;
  return shuffle(pool).slice(0, 4);
}
